/**
 * Formatting for what the API returns.
 *
 * The database stores UTC (context.md section 132). What a person sees is their own choice: their
 * language, their zone, and the shape they read a date in. Those live on the account, and this
 * module is the one place that applies them — a screen calls `formatDate` and never thinks about it.
 *
 * The settings are module state rather than a React context because these are plain functions called
 * from forty places, several of them outside a component. `applyFormatSettings` is called once when
 * the profile arrives and again whenever it changes; everything rendered afterwards uses the new
 * settings, and React re-renders because the preferences that triggered the change are themselves
 * state.
 */

export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
export type TimeFormat = '24h' | '12h';

export interface FormatSettings {
  locale: string;
  /** An IANA zone, or undefined to follow the browser. */
  timeZone: string | undefined;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
}

const DEFAULTS: FormatSettings = {
  locale: 'pt-BR',
  timeZone: undefined,
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
};

let settings: FormatSettings = { ...DEFAULTS };

export function applyFormatSettings(changes: Partial<FormatSettings>): void {
  settings = { ...settings, ...changes };
}

export function formatSettings(): FormatSettings {
  return settings;
}

/**
 * The date parts a chosen shape needs.
 *
 * `Intl` does not take a pattern, and writing one by hand would mean reimplementing a calendar. What
 * it does take is the order and padding, which is all these three shapes differ by — so the parts are
 * asked for and reassembled, and every locale's own digits and calendar still apply.
 */
function datePartsOf(date: Date): { year: string; month: string; day: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(settings.timeZone ? { timeZone: settings.timeZone } : {}),
  }).formatToParts(date);

  const find = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  return { year: find('year'), month: find('month'), day: find('day') };
}

function shapeParts(year: string, month: string, day: string): string {
  switch (settings.dateFormat) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
}

function shapeDate(date: Date): string {
  const { year, month, day } = datePartsOf(date);

  return shapeParts(year, month, day);
}

/**
 * `2026-09-21` — a calendar day, not an instant.
 *
 * A due date, an expected close, a birthday: the database column is a `date` and the value has no
 * time and therefore no zone. `new Date('2026-09-21')` parses it as **midnight UTC**, and rendering
 * that in any zone west of Greenwich shows the day before — every such field in the product read a
 * day early in São Paulo. So a date-only string is never converted; its own digits are shaped.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayOnly(value: string | null | undefined): RegExpExecArray | null {
  return typeof value === 'string' ? DATE_ONLY.exec(value.trim()) : null;
}

function shapeTime(date: Date): string {
  return new Intl.DateTimeFormat(settings.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: settings.timeFormat === '12h',
    ...(settings.timeZone ? { timeZone: settings.timeZone } : {}),
  }).format(date);
}

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | null | undefined): string {
  // A day carries no time; inventing 00:00 for it would be a reading nobody recorded.
  const day = dayOnly(value);

  if (day) return shapeParts(day[1]!, day[2]!, day[3]!);

  const date = parse(value);

  return date ? `${shapeDate(date)} ${shapeTime(date)}` : '—';
}

export function formatDate(value: string | null | undefined): string {
  const day = dayOnly(value);

  if (day) return shapeParts(day[1]!, day[2]!, day[3]!);

  const date = parse(value);

  return date ? shapeDate(date) : '—';
}

export function formatTime(value: string | null | undefined): string {
  const date = parse(value);

  return date ? shapeTime(date) : '—';
}

/** "há 5 min", "ontem" — what a list of conversations needs instead of a timestamp. */
export function formatRelative(value: string | null | undefined): string {
  const date = parse(value);

  if (!date) return '—';

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(settings.locale, { numeric: 'auto' });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }

  return formatter.format(Math.round(seconds), 'second');
}

/**
 * Money.
 *
 * Amounts cross the wire as integer minor units, never as a float — a price is not something to do
 * binary arithmetic on (context.md section 131).
 */
export function formatMoney(minorUnits: number | null | undefined, currency = 'BRL'): string {
  if (minorUnits === null || minorUnits === undefined) return '—';

  return new Intl.NumberFormat(settings.locale, { style: 'currency', currency }).format(
    minorUnits / 100,
  );
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '0 B';

  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;

  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';

  return new Intl.NumberFormat(settings.locale).format(value);
}

/**
 * A calendar day, as a key two dates can be compared on.
 *
 * Local parts rather than `toISOString().slice(0, 10)`: a task due at 21:00 on the 24th in São
 * Paulo is the 25th in UTC, and keying on the ISO string draws it in the wrong column of a week.
 */
export function dayKey(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
